// client/src/hooks/useMyLeagues.js
import { useQuery } from '@tanstack/react-query'
import ApiLeagues from '../api/ApiLeagues'
import { queryKeys } from '../queryClient'
import useAuth from './useAuth'

/**
 * Shared hook for fetching user's enrolled leagues
 * Provides a single source of truth for league data across the application
 *
 * This hook centralizes league data fetching to:
 * - Eliminate duplicate API calls
 * - Provide consistent state across components
 * - Simplify data flow and testing
 *
 * Used by:
 * - useDashboardData (for league count)
 * - useScheduledMatches (for match scheduling)
 * - Any other component needing user's league enrollment
 *
 * @returns {Object} Hook state
 * @returns {Array} leagues - Array of user's enrolled leagues
 * @returns {boolean} loading - Whether data is loading
 * @returns {Error|null} error - Error object if fetch failed
 * @returns {Function} refetch - Function to manually refetch data
 */
function useMyLeagues() {
  const { loading: authLoading } = useAuth()

  const {
    data: leagues = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.myLeagues(),
    queryFn: () => ApiLeagues.getMyLeagues(),
    enabled: !authLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes - league enrollment changes infrequently
  })

  return {
    leagues,
    loading: authLoading || isLoading,
    error,
    refetch,
  }
}

export default useMyLeagues
